import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LEAGUE } from "../../config/league";
import type {
  Direction,
  DraftManagerDTO,
  DraftStateDTO,
  UniverseSearchResult,
} from "../../shared/types";
import { PickAnnounce } from "../components/PickAnnounce";
import { PickClock } from "../components/PickClock";
import { TickerSearch } from "../components/TickerSearch";
import { DirectionTag, ErrorBanner, Pill, Spinner } from "../components/ui";
import { api } from "../lib/api";
import { formatMoney, usePoll } from "../lib/format";

interface CellEdit {
  armed: number | null;
  setArmed: (id: number | null) => void;
  onRemove: (slotId: number) => void;
  onAdd: (slotId: number, manager: string, round: number) => void;
}

export default function DraftPage() {
  const { data, error, refresh } = usePoll(
    api.draftState,
    LEAGUE.draftPollSec * 1000,
  );
  const [authed, setAuthed] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    try {
      return localStorage.getItem("combine_sound") === "1";
    } catch {
      return false;
    }
  });
  const [armed, setArmed] = useState<number | null>(null);
  const [addTarget, setAddTarget] = useState<{
    slotId: number;
    manager: string;
    round: number;
  } | null>(null);
  const [boardErr, setBoardErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .session()
      .then((s) => setAuthed(s.authed))
      .catch(() => setAuthed(false));
  }, []);

  function toggleSound() {
    setSoundOn((on) => {
      const next = !on;
      try {
        localStorage.setItem("combine_sound", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  async function removeAt(slotId: number) {
    setBoardErr(null);
    try {
      await api.removePick(slotId);
      setArmed(null);
      refresh();
    } catch (e) {
      setBoardErr(e instanceof Error ? e.message : String(e));
    }
  }

  const edit: CellEdit | null = authed
    ? {
        armed,
        setArmed,
        onRemove: removeAt,
        onAdd: (slotId, manager, round) => {
          setArmed(null);
          setAddTarget({ slotId, manager, round });
        },
      }
    : null;

  return (
    <div className="space-y-5">
      {data && <PickAnnounce state={data} soundOn={soundOn} />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-fg">
          Live Draft
        </h1>
        <div className="flex items-center gap-2">
          {data && <StatusPill status={data.status} />}
          <button
            onClick={toggleSound}
            title={soundOn ? "Pick sound on" : "Pick sound off"}
            className="rounded-lg border border-edge px-2.5 py-1.5 text-xs font-semibold text-muted hover:text-fg"
          >
            {soundOn ? "🔊" : "🔇"}
          </button>
          <Link
            to="/broadcast/draft"
            className="rounded-lg border border-edge px-3 py-1.5 text-xs font-semibold text-muted hover:text-fg"
          >
            ⛶ Broadcast
          </Link>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
      {boardErr && <ErrorBanner message={boardErr} />}
      {!data && <Spinner label="Loading draft…" />}

      {data && (
        <>
          <OnTheClock state={data} />
          <OrderStrip state={data} />
          <RecentTicker state={data} />
          {edit && (
            <p className="text-xs text-muted">
              Tip: click any pick to reveal ✕ and remove it; empty slots show{" "}
              <span className="text-accent">+ Add</span>.
            </p>
          )}
          <DraftBoard state={data} edit={edit} />
          {authed ? (
            <PickPanel state={data} onChange={refresh} />
          ) : (
            <Pill tone="warn">
              View-only — sign in on the{" "}
              <Link to="/admin" className="underline">
                Admin
              </Link>{" "}
              page to run the draft.
            </Pill>
          )}
        </>
      )}

      {addTarget && (
        <AddPickModal
          target={addTarget}
          onClose={() => setAddTarget(null)}
          onDone={() => {
            setAddTarget(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }: { status: DraftStateDTO["status"] }) {
  if (status === "live") return <Pill tone="live">DRAFTING</Pill>;
  if (status === "done") return <Pill tone="accent">COMPLETE</Pill>;
  return <Pill tone="neutral">NOT STARTED</Pill>;
}

function OnTheClock({ state }: { state: DraftStateDTO }) {
  if (state.status === "done") {
    return (
      <div className="panel p-6 text-center">
        <div className="text-2xl font-extrabold text-accent">
          Draft complete — {state.totalPicks} picks in.
        </div>
      </div>
    );
  }
  return (
    <div className="panel flex flex-wrap items-center justify-between gap-4 p-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-clock">
          On the clock
        </div>
        <div className="mt-1 text-5xl font-black text-fg">
          {state.onTheClock?.name ?? "—"}
        </div>
        {state.nextUp && (
          <div className="mt-2 text-sm text-muted">
            Next up: <span className="font-semibold text-fg">{state.nextUp}</span>
          </div>
        )}
      </div>
      {state.status === "live" && (
        <PickClock startedAt={state.pickStartedAt} seconds={state.pickClockSec} />
      )}
      <div className="num text-right">
        <div className="text-5xl font-black text-fg">
          {Math.min(state.currentPick, state.totalPicks)}
          <span className="text-2xl text-faint">/{state.totalPicks}</span>
        </div>
        <div className="text-sm text-muted">
          Round {state.currentRound} · Pick {state.pickInRound}
        </div>
      </div>
    </div>
  );
}

function OrderStrip({ state }: { state: DraftStateDTO }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-widest text-faint">
        Round {state.currentRound}
      </span>
      {state.order.map((name, i) => {
        const isCurrent = state.onTheClock?.name === name;
        const isNext = state.nextUp === name && !isCurrent;
        return (
          <span
            key={`${name}-${i}`}
            className={`num rounded-lg px-2.5 py-1 text-sm font-semibold ${
              isCurrent
                ? "bg-clock text-ink-950 shadow-clock"
                : isNext
                  ? "border border-clock/50 text-clock"
                  : "bg-ink-700 text-muted"
            }`}
          >
            {i + 1}. {name}
          </span>
        );
      })}
    </div>
  );
}

function RecentTicker({ state }: { state: DraftStateDTO }) {
  if (state.recentPicks.length === 0) return null;
  return (
    <div className="flex items-center gap-2 overflow-x-auto rounded-xl border border-edge bg-ink-900/60 px-3 py-2">
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-faint">
        Recent
      </span>
      {state.recentPicks.map((p) => (
        <span
          key={p.pickNumber}
          className="num flex shrink-0 items-center gap-1.5 rounded-lg bg-ink-800 px-2.5 py-1 text-sm"
        >
          <span className="text-faint">#{p.pickNumber}</span>
          <span className="text-muted">{p.manager}</span>
          <span className="font-bold text-fg">{p.ticker}</span>
          <DirectionTag direction={p.direction} size="sm" />
        </span>
      ))}
    </div>
  );
}

function DraftBoard({
  state,
  edit,
}: {
  state: DraftStateDTO;
  edit: CellEdit | null;
}) {
  const rounds = Array.from({ length: LEAGUE.numRounds }, (_, i) => i + 1);
  return (
    <div className="panel overflow-x-auto p-2">
      <table className="w-full border-separate border-spacing-1">
        <thead>
          <tr>
            <th className="w-10" />
            {state.managers.map((m) => {
              const onClock = state.onTheClock?.id === m.id;
              return (
                <th key={m.id} className="px-1 pb-1 text-center">
                  <Link
                    to={`/manager/${encodeURIComponent(m.name)}`}
                    className={`block truncate text-sm font-bold ${
                      onClock ? "text-clock" : "text-fg"
                    }`}
                  >
                    {m.name}
                  </Link>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rounds.map((round) => (
            <tr key={round}>
              <td className="num text-center text-xs text-faint">{round}</td>
              {state.managers.map((m) => (
                <BoardCell
                  key={m.id}
                  manager={m}
                  round={round}
                  edit={edit}
                  isCurrent={
                    state.status !== "done" &&
                    state.onTheClock?.id === m.id &&
                    state.currentRound === round
                  }
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BoardCell({
  manager,
  round,
  isCurrent,
  edit,
}: {
  manager: DraftManagerDTO;
  round: number;
  isCurrent: boolean;
  edit: CellEdit | null;
}) {
  const pick = manager.picks[round - 1];
  const slotId = manager.slotIds[round - 1] ?? 0;

  if (pick) {
    const armed = edit?.armed === slotId && slotId !== 0;
    const body = (
      <>
        <div className="num text-sm font-bold text-fg">{pick.ticker}</div>
        <div className="flex items-center justify-center gap-1">
          <DirectionTag direction={pick.direction} size="sm" />
          <span className="num text-[10px] text-faint">
            {formatMoney(pick.costBasis)}
          </span>
        </div>
      </>
    );
    if (!edit) {
      return (
        <td className="rounded-lg border border-edge bg-ink-900/70 px-2 py-1.5 text-center align-middle">
          {body}
        </td>
      );
    }
    return (
      <td
        className={`relative rounded-lg border px-2 py-1.5 text-center align-middle ${
          armed ? "border-loss bg-loss/10" : "border-edge bg-ink-900/70"
        }`}
      >
        <button
          type="button"
          onClick={() => edit.setArmed(armed ? null : slotId)}
          className="block w-full"
          title="Click to remove"
        >
          {body}
        </button>
        {armed && (
          <button
            type="button"
            onClick={() => edit.onRemove(slotId)}
            title="Remove this pick"
            className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-loss text-[11px] font-bold text-white shadow-lg hover:brightness-110"
          >
            ✕
          </button>
        )}
      </td>
    );
  }

  if (edit) {
    return (
      <td
        className={`rounded-lg border px-1 py-1.5 text-center align-middle ${
          isCurrent ? "border-clock bg-clock/10" : "border-dashed border-edge/60"
        }`}
      >
        <button
          type="button"
          onClick={() => edit.onAdd(slotId, manager.name, round)}
          className="w-full rounded py-1 text-xs font-semibold text-accent hover:text-fg"
        >
          + Add
        </button>
      </td>
    );
  }

  return (
    <td
      className={`rounded-lg border px-2 py-1.5 text-center align-middle ${
        isCurrent
          ? "animate-pulseclock border-clock bg-clock/10 shadow-clock"
          : "border-dashed border-edge/60"
      }`}
    >
      <span className="text-xs text-faint">{isCurrent ? "on the clock" : "—"}</span>
    </td>
  );
}

function AddPickModal({
  target,
  onClose,
  onDone,
}: {
  target: { slotId: number; manager: string; round: number };
  onClose: () => void;
  onDone: () => void;
}) {
  const [selected, setSelected] = useState<UniverseSearchResult | null>(null);
  const [direction, setDirection] = useState<Direction>("LONG");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function confirm() {
    if (!selected) return;
    setBusy(true);
    setErr(null);
    try {
      await api.editPick({
        slotId: target.slotId,
        ticker: selected.ticker,
        direction,
        price: price ? Number(price) : undefined,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="panel w-full max-w-lg space-y-3 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-muted">
            Add pick — {target.manager} · Round {target.round}
          </h3>
          <button onClick={onClose} className="text-faint hover:text-loss">
            ✕
          </button>
        </div>
        {err && <ErrorBanner message={err} />}
        {selected ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="num rounded-lg bg-ink-700 px-3 py-2 font-bold text-fg">
              {selected.ticker}
              <button
                onClick={() => setSelected(null)}
                className="ml-2 text-faint hover:text-loss"
              >
                ✕
              </button>
            </span>
            <div className="flex overflow-hidden rounded-lg border border-edge">
              {(["LONG", "SHORT"] as Direction[]).map((d) => (
                <button
                  key={d}
                  onClick={() => setDirection(d)}
                  className={`px-4 py-2 text-sm font-bold ${
                    direction === d
                      ? d === "LONG"
                        ? "bg-gain text-ink-950"
                        : "bg-loss text-ink-950"
                      : "text-muted"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
            <input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="price (live if blank)"
              inputMode="decimal"
              className="num w-36 rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint focus:border-accent"
            />
            <button
              onClick={confirm}
              disabled={busy}
              className="rounded-lg bg-accent px-5 py-2 text-sm font-bold text-ink-950 disabled:opacity-50"
            >
              Add pick
            </button>
          </div>
        ) : (
          <TickerSearch onSelect={setSelected} autoFocus placeholder="Search ticker to add…" />
        )}
      </div>
    </div>
  );
}

function PickPanel({
  state,
  onChange,
}: {
  state: DraftStateDTO;
  onChange: () => void;
}) {
  const [selected, setSelected] = useState<UniverseSearchResult | null>(null);
  const [direction, setDirection] = useState<Direction>("LONG");
  const [locked, setLocked] = useState<number | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [manual, setManual] = useState(""); // fallback only if no live price
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const done = state.status === "done";

  // On ticker select, fetch + lock the live price (no typing needed).
  useEffect(() => {
    if (!selected) {
      setLocked(null);
      setManual("");
      return;
    }
    let active = true;
    setQuoting(true);
    setLocked(null);
    setManual("");
    api
      .quote(selected.ticker)
      .then((r) => {
        if (active) setLocked(r.price);
      })
      .catch(() => {
        if (active) setLocked(null);
      })
      .finally(() => {
        if (active) setQuoting(false);
      });
    return () => {
      active = false;
    };
  }, [selected]);

  async function confirm() {
    if (!selected) return;
    const px = locked != null ? locked : manual ? Number(manual) : undefined;
    setBusy(true);
    setErr(null);
    try {
      await api.pick({ ticker: selected.ticker, direction, price: px });
      setSelected(null);
      setLocked(null);
      setManual("");
      setDirection("LONG");
      onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function undo() {
    setBusy(true);
    setErr(null);
    try {
      await api.undo();
      onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function start() {
    setBusy(true);
    try {
      await api.draftControl({ action: "start" });
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function resetClock() {
    setBusy(true);
    try {
      await api.draftControl({ action: "resetClock" });
      onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-muted">
          Commissioner — make the pick
        </h2>
        <div className="flex gap-2">
          {state.status === "pre" && (
            <button
              onClick={start}
              disabled={busy}
              className="rounded-lg bg-gain px-3 py-1.5 text-sm font-bold text-ink-950 disabled:opacity-50"
            >
              Start draft
            </button>
          )}
          {state.status === "live" && (
            <button
              onClick={resetClock}
              disabled={busy}
              className="rounded-lg border border-clock/50 px-3 py-1.5 text-sm font-semibold text-clock hover:bg-clock/10 disabled:opacity-40"
            >
              Reset clock
            </button>
          )}
          <button
            onClick={undo}
            disabled={busy || state.currentPick <= 1}
            className="rounded-lg border border-edge px-3 py-1.5 text-sm font-semibold text-muted hover:text-fg disabled:opacity-40"
          >
            Undo last
          </button>
        </div>
      </div>

      {err && <ErrorBanner message={err} />}

      {done ? (
        <p className="text-sm text-muted">The draft is complete.</p>
      ) : (
        <>
          <div className="text-sm text-muted">
            Picking for{" "}
            <span className="font-bold text-clock">
              {state.onTheClock?.name}
            </span>{" "}
            · Round {state.currentRound}
          </div>

          {selected ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="num rounded-lg bg-ink-700 px-3 py-2 font-bold text-fg">
                {selected.ticker}
                <button
                  onClick={() => setSelected(null)}
                  className="ml-2 text-faint hover:text-loss"
                >
                  ✕
                </button>
              </span>
              <div className="flex overflow-hidden rounded-lg border border-edge">
                {(["LONG", "SHORT"] as Direction[]).map((d) => (
                  <button
                    key={d}
                    onClick={() => setDirection(d)}
                    className={`px-4 py-2 text-sm font-bold ${
                      direction === d
                        ? d === "LONG"
                          ? "bg-gain text-ink-950"
                          : "bg-loss text-ink-950"
                        : "text-muted"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>

              {/* Live price, auto-locked on selection */}
              {quoting ? (
                <span className="num rounded-lg border border-edge px-3 py-2 text-sm text-muted">
                  fetching price…
                </span>
              ) : locked != null ? (
                <span className="num rounded-lg border border-gain/50 bg-gain/10 px-3 py-2">
                  <span className="mr-1.5 text-[10px] uppercase tracking-wide text-faint">
                    locks at
                  </span>
                  <span className="font-bold text-fg">{formatMoney(locked)}</span>
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <span className="num rounded-lg border border-clock/40 bg-clock/10 px-2 py-2 text-xs text-clock">
                    no live price
                  </span>
                  <input
                    value={manual}
                    onChange={(e) => setManual(e.target.value)}
                    placeholder="$ enter"
                    inputMode="decimal"
                    className="num w-28 rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint focus:border-accent"
                  />
                </span>
              )}

              <button
                onClick={confirm}
                disabled={busy || quoting || (locked == null && !manual)}
                className="rounded-lg bg-accent px-5 py-2 text-sm font-bold text-ink-950 disabled:opacity-50"
              >
                Confirm pick
              </button>
            </div>
          ) : (
            <TickerSearch onSelect={setSelected} autoFocus />
          )}
          {selected?.owned && (
            <p className="text-xs text-clock">
              Heads up: {selected.ticker} is already held
              {selected.ownedLong ? " LONG" : ""}
              {selected.ownedShort ? " SHORT" : ""} — the duplicate rule may block
              this.
            </p>
          )}
        </>
      )}
    </div>
  );
}
